import { FC } from 'react';

import { Icon } from 'src/general/Icons/Icon';
import { CTAButton } from 'src/general/components/Button/CTAButton';
import { Image } from 'src/general/components/Image/Image';
import { Link } from 'src/general/components/Link/Link';
import { Shade } from 'src/general/components/Shade/Shade';
import { getLinkType } from 'src/general/helpers/getLinkType';

import { HeaderMenuNavigationItem } from '../HeaderMenu.type';

const generateSubmenuDataTestId = (href: string): string => {
  const urlPart = href.split('/').pop() || 'submenu';
  return `why-carma-submenu-${urlPart}`;
};

interface Props {
  submenu: Extract<HeaderMenuNavigationItem, { type: 'submenu_faq' }>;
}

export const SubmenuFaq: FC<Props> = ({ submenu: { submenu: submenuContent } }) => {
  return (
    <nav className="w-full bg-bac-tie1 md:flex">
      <div className="md:h-full md:w-2/3 md:border-r md:border-r-bor-neu-mut">
        <header className="flex items-center justify-between gap-2 border-b border-b-bor-neu-mut px-4 pb-4 md:border-b-0 md:bg-bac-tie2 md:px-6 md:py-4">
          <h2 className="c-fs-body1 font-extrabold">{submenuContent.title}</h2>
          {submenuContent.ctas.length > 0 && <CTAButton {...submenuContent.ctas[0]} size="small" />}
        </header>
        <ul className="flex list-none flex-col gap-1 border-b border-b-bor-neu-mut py-4 md:grid md:grid-cols-2 md:border-b-0 md:px-4">
          {submenuContent.items.map((item) => {
            const dataTestId = generateSubmenuDataTestId(item.href);
            return (
              <li key={item.href}>
                <Link
                  href={item.href}
                  className="c-shade_container c-shade_container--grey group relative flex w-full items-center gap-2 overflow-hidden rounded-xs py-2 pr-10 pl-2 no-underline"
                  data-testid={dataTestId}
                >
                  <Shade noBackground />
                  {!!item.image && <Image src={item.image} width={40} height={40} alt="" />}
                  <div>
                    <div className="c-fs-body1" data-testid={`${generateSubmenuDataTestId(item.href)}-hover`}>
                      {item.label}
                    </div>
                    {!!item.subtitle && <div className="c-fs-body2 text-tex-def-tie2">{item.subtitle}</div>}
                  </div>
                  <div className="absolute top-1/2 right-4 -translate-y-1/2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                    <div className="-translate-x-4 transition-transform duration-200 group-hover:translate-x-0">
                      <Icon name="arrow-right" />
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="p-4 md:w-1/3 md:py-8">
        <h3 className="c-fs-overline">{submenuContent.titleFaq}</h3>
        <ul className="flex list-none flex-col gap-4 py-4 md:gap-6 md:py-6">
          {submenuContent.itemsFaq.map((item) => (
            <li key={item.href}>
              <Link
                href={item.href}
                className="c-fs-body2 no-underline hover:underline"
                target={getLinkType(item.href) === 'external' ? '_blank' : undefined}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
        {submenuContent.ctasFaq.length > 0 && <CTAButton {...submenuContent.ctasFaq[0]} size="small" />}
      </div>
    </nav>
  );
};
